import React, { useState, useEffect } from 'react'
import Cookies from 'universal-cookie'
import { Link, useNavigate } from 'react-router-dom'
import { useGoogleLogin } from '@react-oauth/google'
import { UserContext } from '../../context/UserProvider'

// Local import
import { withContext } from '../../context/withContext'
import { Input } from '../../Components/FormGroups'
import { API, updateHeaders, ADMIN_GOOGLE_HOSTED_DOMAIN, ADMIN_PASSWORD_LOGIN_ENABLED } from '../../utils/config'
import { LoginAndTokenData } from '../../helpers/essentials'

const cookies = new Cookies()

function Login(props) {
	const [username, setUsername] = useState('')
	const [password, setPassword] = useState('')
	const [brokerModal, toggleBrokerModal] = useState(false)
	const [errData, setErrData] = useState({})
	const [alert, showAlert] = useState(false)
	const [loading, showLoading] = useState(false)
	const [loadingGoogle, setLoadingGoogle] = useState(false)
	const [darkMode, setDarkMode] = useState(false)
	const navigate = useNavigate()
	const brand = props.context.brand

	// Check for saved dark mode preference or default to system preference
	useEffect(() => {
		const savedTheme = localStorage.getItem('theme')
		const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
		const isDark = savedTheme === 'dark' || (!savedTheme && prefersDark)
		setDarkMode(isDark)
		if (isDark) {
			document.documentElement.classList.add('dark')
		} else {
			document.documentElement.classList.remove('dark')
		}
	}, [])

	const toggleDarkMode = () => {
		const newDarkMode = !darkMode
		setDarkMode(newDarkMode)
		if (newDarkMode) {
			document.documentElement.classList.add('dark')
			localStorage.setItem('theme', 'dark')
		} else {
			document.documentElement.classList.remove('dark')
			localStorage.setItem('theme', 'light')
		}
	}

	const completeLogin = (response) => {
		cookies.set('toggle', false, { path: '/' })
		cookies.set('token', response.data.token, { path: '/' })
		cookies.set('company', response.data.company.company.uid, {
			path: '/',
		})
		let newData = LoginAndTokenData(response, 'Login')
		updateHeaders({ Authorization: `Token ${response.data.token}` })
		props.updateContextVal(newData)
		navigate('/')
	}

	// Login con Google Workspace. El backend (/intranet/google-login/) valida que
	// la cuenta sea del dominio permitido y que el usuario exista como staff.
	const googleLogin = useGoogleLogin({
		flow: 'auth-code',
		redirect_uri: 'postmessage',
		hosted_domain: ADMIN_GOOGLE_HOSTED_DOMAIN,
		scope: 'https://www.googleapis.com/auth/userinfo.email openid',
		onSuccess: (codeResponse) => {
			showAlert(false)
			setLoadingGoogle(true)
			API.post('/intranet/google-login/', { code: codeResponse.code })
				.then((response) => completeLogin(response))
				.catch((err) => {
					const message =
						err.response?.data?.res ||
						'No se pudo iniciar sesión con Google. Verifica que uses tu cuenta de AlterEstate.'
					setErrData(message)
					showAlert(true)
				})
				.finally(() => setLoadingGoogle(false))
		},
		onError: (err) => {
			console.log('❌ Google OAuth error:', err)
			setErrData('No se pudo completar el inicio de sesión con Google. Intenta de nuevo.')
			showAlert(true)
		},
	})

	const handleGoogleLogin = () => {
		showAlert(false)
		try {
			googleLogin()
		} catch (err) {
			console.log('❌ Google OAuth not ready:', err)
			setErrData('Google Sign-In aún no está disponible. Intenta nuevamente en unos segundos.')
			showAlert(true)
		}
	}

	const handleLogin = (e) => {
		e.preventDefault()
		showAlert(false)
		showLoading(true)
		API.post('/intranet/login/', { username: username.replace(' ', ''), password: password })
			.then((response) => {
				showLoading(false)
				completeLogin(response)
			})
			.catch((err) => {
				let errorData = err.response?.data
				try {
					showLoading(false)
					if (err.response?.data?.user_type === '3' || err.response?.data?.user_type === '5') {
						toggleBrokerModal(true)
					} else {
						setErrData(errorData.res)
						showAlert(true)
					}
				} catch (error) {
					console.log(error)
				}
			})
	}

	const primaryColor = brand.primary_color || '#457ecf'

	return (
		<div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-gradient-to-br tw-from-gray-50 tw-via-white tw-to-gray-100 dark:tw-from-gray-900 dark:tw-via-gray-800 dark:tw-to-gray-900 tw-py-12 tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-transition-colors tw-duration-300">
			{/* Dark Mode Toggle */}
			<button
				onClick={toggleDarkMode}
				className="tw-fixed tw-top-4 tw-right-4 tw-p-3 tw-rounded-full tw-bg-white dark:tw-bg-gray-800 tw-shadow-lg hover:tw-shadow-xl tw-transition-all tw-duration-300 tw-z-50 tw-border tw-border-gray-200 dark:tw-border-gray-700">
				{darkMode ? (
					<svg
						className="tw-w-5 tw-h-5 tw-text-yellow-500"
						fill="currentColor"
						viewBox="0 0 20 20"
						xmlns="http://www.w3.org/2000/svg">
						<path
							fillRule="evenodd"
							d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
							clipRule="evenodd"
						/>
					</svg>
				) : (
					<svg
						className="tw-w-5 tw-h-5 tw-text-gray-700 dark:tw-text-gray-300"
						fill="currentColor"
						viewBox="0 0 20 20"
						xmlns="http://www.w3.org/2000/svg">
						<path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
					</svg>
				)}
			</button>

			<div className="tw-max-w-md tw-w-full">
				{/* Logo */}
				<div className="tw-text-center tw-mb-8 tw-animate-fade-in">
					<img
						src="https://alterestate.com/wp-content/uploads/2025/08/Asset-6@150x-8-1024x318.png"
						className="tw-mx-auto tw-pt-4 tw-max-h-20 tw-object-contain tw-filter dark:tw-brightness-0 dark:tw-invert"
					/>
				</div>

				{/* Login Form */}
				<div className="tw-bg-white dark:tw-bg-gray-800 tw-rounded-2xl tw-shadow-2xl tw-p-8 tw-border tw-border-gray-200 dark:tw-border-gray-700 tw-transition-all tw-duration-300 hover:tw-shadow-3xl">
					<h2 className="tw-text-3xl tw-font-bold tw-text-gray-900 dark:tw-text-white tw-mb-2 tw-text-center tw-tracking-tight">
						Iniciar Sesión
					</h2>
					<p className="tw-text-center tw-text-gray-600 dark:tw-text-gray-400 tw-mb-8 tw-text-sm">
						{ADMIN_PASSWORD_LOGIN_ENABLED
							? 'Ingresa tus credenciales para continuar'
							: `Acceso exclusivo con tu cuenta de Google @${ADMIN_GOOGLE_HOSTED_DOMAIN}`}
					</p>

					{/* Google Workspace */}
					<button
						type="button"
						onClick={handleGoogleLogin}
						disabled={loadingGoogle || loading}
						className="tw-w-full tw-py-3.5 tw-px-4 tw-flex tw-items-center tw-justify-center tw-gap-3 tw-rounded-xl tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-bg-white dark:tw-bg-gray-900 tw-text-base tw-font-semibold tw-text-gray-800 dark:tw-text-gray-100 tw-shadow-sm hover:tw-bg-gray-50 dark:hover:tw-bg-gray-800 hover:tw-shadow-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-offset-white dark:focus:tw-ring-offset-gray-800 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-all tw-duration-200">
						{loadingGoogle ? (
							<svg
								className="tw-animate-spin tw-h-5 tw-w-5"
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24">
								<circle
									className="tw-opacity-25"
									cx="12"
									cy="12"
									r="10"
									stroke="currentColor"
									strokeWidth="4"></circle>
								<path
									className="tw-opacity-75"
									fill="currentColor"
									d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
							</svg>
						) : (
							<svg className="tw-h-5 tw-w-5" viewBox="0 0 48 48" aria-hidden="true">
								<path
									fill="#EA4335"
									d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
								/>
								<path
									fill="#4285F4"
									d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
								/>
								<path
									fill="#FBBC05"
									d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
								/>
								<path
									fill="#34A853"
									d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
								/>
							</svg>
						)}
						{loadingGoogle ? 'Validando cuenta...' : 'Continuar con Google'}
					</button>

					{alert && (
						<div className="tw-mt-6 tw-bg-red-50 dark:tw-bg-red-900/20 tw-border tw-border-red-200 dark:tw-border-red-800 tw-text-red-700 dark:tw-text-red-400 tw-px-4 tw-py-3 tw-rounded-lg tw-text-center tw-animate-slide-in">
							<div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
								<svg
									className="tw-w-5 tw-h-5"
									fill="currentColor"
									viewBox="0 0 20 20"
									xmlns="http://www.w3.org/2000/svg">
									<path
										fillRule="evenodd"
										d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
										clipRule="evenodd"
									/>
								</svg>
								{errData}
							</div>
						</div>
					)}

					{ADMIN_PASSWORD_LOGIN_ENABLED && (
						<>
							<div className="tw-flex tw-items-center tw-gap-3 tw-my-6">
								<div className="tw-flex-1 tw-h-px tw-bg-gray-200 dark:tw-bg-gray-700" />
								<span className="tw-text-xs tw-uppercase tw-tracking-wide tw-text-gray-400">
									o con contraseña
								</span>
								<div className="tw-flex-1 tw-h-px tw-bg-gray-200 dark:tw-bg-gray-700" />
							</div>

							<form onSubmit={handleLogin} className="tw-space-y-6">
								<div className="tw-space-y-5">
									<Input
										required
										type="text"
										label="Email"
										id="username"
										placeholder="Correo electrónico"
										value={username}
										onChange={(e) => setUsername(e.target.value.toLowerCase().replace(' ', ''))}
									/>

									<Input
										required
										type="password"
										label="Contraseña"
										id="password"
										placeholder="Password"
										value={password}
										onChange={(e) => setPassword(e.target.value)}
									/>
								</div>

								<button
									type="submit"
									disabled={loading}
									style={{ backgroundColor: primaryColor, borderColor: primaryColor }}
									className="tw-w-full tw-py-3.5 tw-px-4 tw-border tw-border-transparent tw-rounded-xl tw-shadow-lg tw-text-base tw-font-semibold tw-text-white hover:tw-opacity-90 hover:tw-shadow-xl focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-offset-white dark:focus:tw-ring-offset-gray-800 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-all tw-duration-200 tw-transform hover:tw-scale-[1.02] active:tw-scale-[0.98]">
									{loading ? (
										<span className="tw-flex tw-items-center tw-justify-center">
											<svg
												className="tw-animate-spin tw--ml-1 tw-mr-3 tw-h-5 tw-w-5 tw-text-white"
												xmlns="http://www.w3.org/2000/svg"
												fill="none"
												viewBox="0 0 24 24">
												<circle
													className="tw-opacity-25"
													cx="12"
													cy="12"
													r="10"
													stroke="currentColor"
													strokeWidth="4"></circle>
												<path
													className="tw-opacity-75"
													fill="currentColor"
													d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
											</svg>
											Procesando...
										</span>
									) : (
										<span className="tw-flex tw-items-center tw-justify-center tw-gap-2">
											<svg
												className="tw-w-5 tw-h-5"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
												xmlns="http://www.w3.org/2000/svg">
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
												/>
											</svg>
											Acceder
										</span>
									)}
								</button>
							</form>
						</>
					)}
				</div>
			</div>

			{/* Broker Modal */}
			{brokerModal && (
				<div className="tw-fixed tw-inset-0 tw-z-50 tw-overflow-y-auto">
					<div className="tw-flex tw-items-center tw-justify-center tw-min-h-screen tw-px-4 tw-pt-4 tw-pb-20 tw-text-center sm:tw-block sm:tw-p-0">
						{/* Backdrop */}
						<div
							className="tw-fixed tw-inset-0 tw-transition-opacity tw-bg-gray-900 tw-bg-opacity-75 dark:tw-bg-opacity-90"
							onClick={() => toggleBrokerModal(false)}></div>

						{/* Modal Panel */}
						<div className="tw-inline-block tw-align-bottom tw-bg-white dark:tw-bg-gray-800 tw-rounded-2xl tw-text-left tw-overflow-hidden tw-shadow-2xl tw-transform tw-transition-all sm:tw-my-8 sm:tw-align-middle sm:tw-max-w-lg tw-w-full tw-border tw-border-gray-200 dark:tw-border-gray-700">
							{/* Modal Header */}
							<div className="tw-flex tw-items-center tw-justify-between tw-px-6 tw-py-5 tw-border-b tw-border-gray-200 dark:tw-border-gray-700 tw-bg-gray-50 dark:tw-bg-gray-900/50">
								<h3 className="tw-text-xl tw-font-semibold tw-text-gray-900 dark:tw-text-white">
									Aviso Importante
								</h3>
								<button
									onClick={() => toggleBrokerModal(false)}
									className="tw-text-gray-400 hover:tw-text-gray-600 dark:hover:tw-text-gray-300 tw-transition-colors tw-p-1 tw-rounded-lg hover:tw-bg-gray-100 dark:hover:tw-bg-gray-700">
									<span className="tw-sr-only">Cerrar</span>
									<svg
										className="tw-h-6 tw-w-6"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor">
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M6 18L18 6M6 6l12 12"
										/>
									</svg>
								</button>
							</div>

							{/* Modal Body */}
							<div className="tw-px-6 tw-py-6">
								<div className="tw-bg-yellow-50 dark:tw-bg-yellow-900/20 tw-border tw-border-yellow-200 dark:tw-border-yellow-800 tw-rounded-xl tw-p-4 tw-mb-6">
									<div className="tw-text-center">
										<strong className="tw-text-yellow-800 dark:tw-text-yellow-400 tw-text-lg">
											AVISO IMPORTANTE
										</strong>
									</div>
								</div>

								<p className="tw-text-gray-700 dark:tw-text-gray-300 tw-mb-6 tw-leading-relaxed">
									La cuenta con la cual estás intentando acceder, es una cuenta de Broker Externo por
									lo tanto debes de acceder al Área de Brokers haciendo{' '}
									<a
										href="https://brokers.alterestate.com/login?utm_referer=Dashboard"
										target="_blank"
										rel="noopener noreferrer"
										className="tw-text-blue-600 dark:tw-text-blue-400 hover:tw-text-blue-800 dark:hover:tw-text-blue-300 tw-underline tw-font-medium">
										click aquí
									</a>
									, esta parte de la plataforma es exclusiva para clientes de AlterEstate.
								</p>

								<a
									href="https://brokers.alterestate.com/login?utm_referer=Dashboard"
									target="_blank"
									rel="noopener noreferrer"
									className="tw-block tw-w-full tw-mb-4">
									<button
										disabled
										className="tw-w-full tw-py-3.5 tw-px-4 tw-bg-[#08a742] hover:tw-opacity-80 tw-text-white tw-font-semibold tw-rounded-xl tw-transition-all tw-text-lg tw-mb-4 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-shadow-lg hover:tw-shadow-xl tw-transform hover:tw-scale-[1.02] active:tw-scale-[0.98]">
										Ir al Área de Brokers
									</button>
								</a>

								<div className="tw-text-center tw-pt-2">
									<Link
										to="/registro"
										className="tw-text-sm tw-text-blue-600 dark:tw-text-blue-400 hover:tw-text-blue-800 dark:hover:tw-text-blue-300 tw-underline tw-font-medium">
										o regístrate en AlterEstate - CRM Inmobiliario
									</Link>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}

export default withContext(Login)
